import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Checkbox, Flex } from 'antd';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { useClassifierDataTypesBy } from '../../../classifier';
import { AddRemoveButtons } from '../../../common/components';
import {
  FormField,
  FormInputNumber,
  useAdditionalFormContext,
} from '../../../common/form';
import { createAlgorithmMappingsOptionGroup } from '../mappings/utils';
import { AlgorithmSourceDimensionFormDataType } from '../sources/schema/types';
import { createAlgorithmSourceDimensionsOptionGroup } from '../sources/utils';
import { useAlgorithmFormValues } from '../useAlgorithmFormValues';
import { AlgorithmJoinItemCategorySelectField } from './AlgorithmJoinItemCategorySelectField';
import { AlgorithmJoinItemDatePicker } from './AlgorithmJoinItemDatePicker';
import { AlgorithmJoinItemDbColumnField } from './AlgorithmJoinItemDbColumnField';
import { AlgorithmJoinItemMappingValueSelectField } from './AlgorithmJoinItemMappingValueSelectField';
import translations from './AlgorithmJoinItemFormFields.translations.json';
import { AlgorithmJoinItemSourceDimensionOrMappingSelectField } from './AlgorithmJoinItemSourceDimensionOrMappingSelectField';
import { AlgorithmJoinItemTargetDimensionOrMappingSelectField } from './AlgorithmJoinItemTargetDimensionOrMappingSelectField';
import { AlgorithmJoinsFormRowControls } from './AlgorithmJoinsFormRowControls';
import { AlgorithmJoinsFormStore } from './AlgorithmJoinsFormStore';
import { getDefaultAlgorithmJoinItemFormData } from './schema/defaultValues';
import { AlgorithmJoinsValidFormDataType } from './schema/types';
import { useAutoPairSubjectDimensions } from './useAutoPairSubjectDimensions';
import { useUsedAlgorithmSourceIds } from './useUsedAlgorithmSourceIds';
import {
  changeAlgorithmJoinItemFormDataPerConstantSwitch,
  changeAlgorithmJoinItemFormDataPerDimensionClassifierCodeFormatChange,
  getSelectedDimension,
  isDimensionWithCategories,
  useIsDimensionWithDataType,
  useWatchAlgorithmJoinValue,
} from './utils';

const { addTranslations, useTranslation } = i18nNsFactory(
  'algorithm-join-item-form-fields',
);
addTranslations(translations);

export interface AlgorithmJoinItemFormFieldsRef2 {
  autoPairSubjectDimensionsIfPossible: () => void;
}

export interface AlgorithmJoinItemFormFieldsProps {
  joinIndex: number;
  index: number;
  count: number;
  isLast: boolean;
}

export const AlgorithmJoinItemFormFields = forwardRef<
  AlgorithmJoinItemFormFieldsRef2,
  AlgorithmJoinItemFormFieldsProps
>((props, ref) => {
  const { joinIndex, index, count, isLast } = props;
  const { t } = useTranslation();
  const baseName = `joins.${joinIndex}.algorithmJoinItems` as const;
  const [sources] = AlgorithmJoinsFormStore.useValue('sources');
  const [mappings] = AlgorithmJoinsFormStore.useValue('mappings');
  const [isDbSources] = AlgorithmJoinsFormStore.useValue('isDbSources');

  const [algorithmSourceId] = useAlgorithmFormValues([
    `joins.${joinIndex}.algorithmSourceId`,
  ] as const);

  const { setValue, getValues, clearErrors } =
    useFormContext<AlgorithmJoinsValidFormDataType>();
  const [isSourceConstant, setIsSourceConstant] = useState(() =>
    Boolean('sourceValue' in getValues(`${baseName}.${index}`)),
  );
  const [isTargetConstant, setIsTargetConstant] = useState(() =>
    Boolean('targetValue' in getValues(`${baseName}.${index}`)),
  );
  const { onChange: formOnChange } = useAdditionalFormContext();

  const addAlgorithmJoinItem = useCallback(() => {
    setValue(
      `${baseName}.${index + 1}`,
      getDefaultAlgorithmJoinItemFormData(),
      { shouldDirty: true },
    );
    formOnChange?.();
  }, [setValue, baseName, index, formOnChange]);

  const removeAlgorithmJoinItem = useCallback(() => {
    clearErrors(`${baseName}.${index}`);
    setValue(baseName, getValues(baseName).toSpliced(index, 1), {
      shouldDirty: true,
    });
    formOnChange?.();
  }, [setValue, baseName, index, getValues, clearErrors, formOnChange]);

  const targetDimensionOptions = useMemo(
    () =>
      sources
        .filter(
          (s) =>
            s.dimensions.length &&
            algorithmSourceId &&
            s['id'] === algorithmSourceId,
        )
        .map((s, i) => createAlgorithmSourceDimensionsOptionGroup(s, i)),
    [sources, algorithmSourceId],
  );

  const usedAlgorithmSourceIds = useUsedAlgorithmSourceIds({
    untilIndex: joinIndex,
  });
  const sourceDimensionOptions = useMemo(
    () =>
      sources
        .filter(
          (s) =>
            s.dimensions.length && usedAlgorithmSourceIds.includes(s['id']),
        )
        .map((s, i) => createAlgorithmSourceDimensionsOptionGroup(s, i)),
    [sources, usedAlgorithmSourceIds],
  );

  const variant = useWatchAlgorithmJoinValue(`${baseName}.${index}._variant`);
  const algorithmMappingAlias = useWatchAlgorithmJoinValue(
    `${baseName}.${index}.algorithmMappingAlias`,
  );
  const isSourceMapping = variant?.startsWith('MAPPING_');
  const isTargetMapping = variant?.endsWith('_MAPPING');
  const sourceMappingAlias = isSourceMapping
    ? algorithmMappingAlias
    : undefined;
  const targetMappingAlias = isTargetMapping
    ? algorithmMappingAlias
    : undefined;

  const sourceDimensionAndMappingOptions = useMemo(
    () =>
      !isTargetMapping && mappings.length
        ? [
            createAlgorithmMappingsOptionGroup(mappings),
            ...sourceDimensionOptions,
          ]
        : sourceDimensionOptions,
    [mappings, sourceDimensionOptions, isTargetMapping],
  );

  const targetDimensionAndMappingOptions = useMemo(
    () =>
      !isSourceMapping && mappings.length
        ? [
            ...targetDimensionOptions,
            createAlgorithmMappingsOptionGroup(mappings),
          ]
        : targetDimensionOptions,
    [targetDimensionOptions, mappings, isSourceMapping],
  );

  const classifierDataTypesById = useClassifierDataTypesBy('id');

  const setOppositeCodeFormatWhenApplicable = useCallback(
    (dimension?: AlgorithmSourceDimensionFormDataType) => {
      setValue(
        `${baseName}.${index}`,
        changeAlgorithmJoinItemFormDataPerDimensionClassifierCodeFormatChange(
          getValues(`${baseName}.${index}`),
          classifierDataTypesById,
          dimension,
        ),
        { shouldDirty: true },
      );
    },
    [getValues, setValue, baseName, index, classifierDataTypesById],
  );

  const autoPairSubjectDimensionsIfPossible = useAutoPairSubjectDimensions(
    sourceDimensionOptions,
    targetDimensionOptions,
    joinIndex,
    count,
  );

  useImperativeHandle(
    ref,
    () => ({
      autoPairSubjectDimensionsIfPossible,
    }),
    [autoPairSubjectDimensionsIfPossible],
  );

  const [sourceDimension, setSourceDimension] = useState(() =>
    getSelectedDimension(
      sourceDimensionOptions,
      ...getValues([
        `${baseName}.${index}.algorithmSourceId`,
        `${baseName}.${index}.sourceDimension`,
      ]),
    ),
  );
  const [targetDimension, setTargetDimension] = useState(() =>
    getSelectedDimension(
      targetDimensionOptions,
      ...getValues([
        `joins.${joinIndex}.algorithmSourceId`,
        `${baseName}.${index}.targetDimension`,
      ]),
    ),
  );

  useEffect(() => {
    if (sourceDimension || targetDimension) {
      setOppositeCodeFormatWhenApplicable(sourceDimension || targetDimension);
    }
  }, [setOppositeCodeFormatWhenApplicable, sourceDimension, targetDimension]);

  const resetItemFields = useCallback(
    (role: 'source' | 'target', isConstant: boolean) => {
      setValue(
        `${baseName}.${index}`,
        changeAlgorithmJoinItemFormDataPerConstantSwitch(
          getValues(`${baseName}.${index}`),
          role,
          isConstant,
          classifierDataTypesById,
          role === 'source' ? targetDimension : sourceDimension,
        ),
        { shouldDirty: true },
      );
    },
    [
      baseName,
      index,
      setValue,
      getValues,
      classifierDataTypesById,
      targetDimension,
      sourceDimension,
    ],
  );

  const isDimensionWithDateDataType = useIsDimensionWithDataType('DATE');
  const isDimensionWithNumberDataType = useIsDimensionWithDataType('NUMBER');

  return (
    <Wrapper gap="small">
      {!isDbSources && isSourceConstant && isTargetMapping ? (
        <AlgorithmJoinItemMappingValueSelectField
          joinIndex={joinIndex}
          index={index}
          role="source"
          mappingAlias={targetMappingAlias}
        />
      ) : isSourceConstant && isDimensionWithDateDataType(targetDimension) ? (
        <AlgorithmJoinItemDatePicker
          joinIndex={joinIndex}
          index={index}
          role="source"
        />
      ) : isSourceConstant && isDimensionWithCategories(targetDimension) ? (
        <AlgorithmJoinItemCategorySelectField
          joinIndex={joinIndex}
          index={index}
          role="source"
          dimension={targetDimension}
        />
      ) : isSourceConstant ? (
        <StyledFormField
          required
          label={t('source-constant')}
          name={`${baseName}.${index}.sourceValue`}
        >
          {isDimensionWithNumberDataType(targetDimension) ? (
            <FormInputNumber name={`${baseName}.${index}.sourceValue`} />
          ) : undefined}
        </StyledFormField>
      ) : isDbSources ? (
        <StyledAlgorithmJoinItemDbColumnField
          required
          label={t('source-dimension')}
          joinIndex={joinIndex}
          joinItemIndex={index}
          side="source"
          mappings={mappings}
        />
      ) : (
        <AlgorithmJoinItemSourceDimensionOrMappingSelectField
          joinIndex={joinIndex}
          index={index}
          options={sourceDimensionAndMappingOptions}
          onChange={(_value, option) => setSourceDimension(option?.dimension)}
        />
      )}
      <AlgorithmJoinsFormRowControls>
        <Checkbox
          id={`algorithmJoinItem-control-${joinIndex}-${index}-constant-source`}
          onChange={(event) => {
            setIsSourceConstant(event.target.checked);
            resetItemFields('source', event.target.checked);
            if (event.target.checked) {
              setSourceDimension(undefined);
              setIsTargetConstant(false);
            }
            formOnChange?.();
          }}
          checked={isSourceConstant}
        >
          {t('constant')}
        </Checkbox>
      </AlgorithmJoinsFormRowControls>
      {!isDbSources && isTargetConstant && isSourceMapping ? (
        <AlgorithmJoinItemMappingValueSelectField
          joinIndex={joinIndex}
          index={index}
          role="target"
          mappingAlias={sourceMappingAlias}
        />
      ) : isTargetConstant && isDimensionWithDateDataType(sourceDimension) ? (
        <AlgorithmJoinItemDatePicker
          joinIndex={joinIndex}
          index={index}
          role="target"
        />
      ) : isTargetConstant && isDimensionWithCategories(sourceDimension) ? (
        <AlgorithmJoinItemCategorySelectField
          joinIndex={joinIndex}
          index={index}
          role="target"
          dimension={sourceDimension}
        />
      ) : isTargetConstant ? (
        <StyledFormField
          required
          label={t('target-constant')}
          name={`${baseName}.${index}.targetValue`}
        >
          {isDimensionWithNumberDataType(sourceDimension) ? (
            <FormInputNumber name={`${baseName}.${index}.targetValue`} />
          ) : undefined}
        </StyledFormField>
      ) : isDbSources ? (
        <StyledAlgorithmJoinItemDbColumnField
          required
          label={t('target-dimension')}
          joinIndex={joinIndex}
          joinItemIndex={index}
          side="target"
          mappings={mappings}
        />
      ) : (
        <AlgorithmJoinItemTargetDimensionOrMappingSelectField
          joinIndex={joinIndex}
          index={index}
          options={targetDimensionAndMappingOptions}
          onChange={(_value, option) => setTargetDimension(option?.dimension)}
        />
      )}
      <AlgorithmJoinsFormRowControls>
        <Checkbox
          id={`algorithmJoinItem-control-${joinIndex}-${index}-constant-target`}
          onChange={(event) => {
            setIsTargetConstant(event.target.checked);
            resetItemFields('target', event.target.checked);
            if (event.target.checked) {
              setTargetDimension(undefined);
              setIsSourceConstant(false);
            }
            formOnChange?.();
          }}
          checked={isTargetConstant}
        >
          {t('constant')}
        </Checkbox>
        <AddRemoveButtons
          id={`algorithmJoinItem-control-${joinIndex}-${index}`}
          rowName={t('dimension')}
          onClickAdd={isLast ? addAlgorithmJoinItem : undefined}
          onClickRemove={count > 1 ? removeAlgorithmJoinItem : undefined}
        />
      </AlgorithmJoinsFormRowControls>
    </Wrapper>
  );
});

const Wrapper = styled(Flex)`
  display: grid;
  grid-template-columns: 1fr auto 1fr auto;
`;

const StyledFormField = styled(FormField)`
  > div {
    align-items: center;
    flex-grow: 1;
  }
`;

const StyledAlgorithmJoinItemDbColumnField = styled(
  AlgorithmJoinItemDbColumnField,
)`
  > div {
    align-items: center;
    flex-grow: 1;
  }
`;
